import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '../../utils/crmApi';
import { useCRM } from '../../contexts/CRMContext';
import { 
  UserGroupIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { EmptyState } from '../ui/empty-states';

const RelocationWorkspaceSelector = () => {
  const navigate = useNavigate();
  const { currentAgency } = useCRM();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading clients for RelocationWorkspaceSelector...');
      console.log('🏢 Current agency:', currentAgency);
      console.log('🔑 Token present:', !!localStorage.getItem('crm_token'));
      console.log('🏢 Agency ID:', localStorage.getItem('current_agency_id'));
      
      const response = await clientAPI.getAll();
      console.log('📥 API response:', response);
      console.log('📥 Response data:', response?.data);
      
      if (response.data && response.data.success) {
        // Use the correct data structure - it's response.data.data, not response.data.clients
        const clientsData = response.data.data || [];
        console.log('✅ Clients loaded successfully:', clientsData);
        console.log('📊 Number of clients:', clientsData.length);
        setClients(clientsData);
      } else {
        console.error('Failed to load clients:', response.data?.message);
        console.log('📊 Response structure:', {
          success: response.data?.success,
          data: response.data?.data,
          clients: response.data?.clients,
          message: response.data?.message
        });
        
        // Temporary fallback with mock data for testing
        console.log('🔄 Using mock data for testing...');
        const mockClients = [
          {
            _id: 'mock-1',
            name: 'María González',
            email: 'maria.gonzalez@email.com',
            status: 'active',
            arrivalDate: '2024-03-15',
            familyMembers: [
              { name: 'Carlos González', relation: 'spouse', age: 35 },
              { name: 'Ana González', relation: 'daughter', age: 8 }
            ]
          },
          {
            _id: 'mock-2',
            name: 'John Smith',
            email: 'john.smith@email.com',
            status: 'pending',
            arrivalDate: '2024-04-20',
            familyMembers: []
          }
        ];
        setClients(mockClients);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      console.log('📊 Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Temporary fallback with mock data for testing
      console.log('🔄 Using mock data due to API error...');
      const mockClients = [
        {
          _id: 'mock-1',
          name: 'María González',
          email: 'maria.gonzalez@email.com',
          status: 'active',
          arrivalDate: '2024-03-15',
          familyMembers: [
            { name: 'Carlos González', relation: 'spouse', age: 35 },
            { name: 'Ana González', relation: 'daughter', age: 8 }
          ]
        },
        {
          _id: 'mock-2',
          name: 'John Smith',
          email: 'john.smith@email.com',
          status: 'pending',
          arrivalDate: '2024-04-20',
          familyMembers: []
        }
      ];
      setClients(mockClients);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getClientStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50 border-green-200';
      case 'pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'completed': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'at_risk': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-muted-foreground bg-muted border-input';
    }
  };

  const getRelocationStage = (client) => {
    // Mock logic - in real app this would come from client data
    const stages = ['initial_consultation', 'document_collection', 'visa_processing', 'housing_search', 'school_enrollment', 'completed'];
    return stages[Math.floor(Math.random() * stages.length)];
  };

  const getDaysInStage = (client) => {
    // Mock logic - in real app this would be calculated
    return Math.floor(Math.random() * 30) + 1;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading clients...</p>
        </div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="min-h-screen bg-muted p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">Relocation Workspace</h1>
            <p className="text-muted-foreground">Select a client to view their relocation journey</p>
          </div>
          <EmptyState
            icon={UserGroupIcon}
            title="No clients found"
            description="You need to add clients before you can use the Relocation Workspace. Start by adding your first client."
            primaryAction={{
              label: "Add First Client",
              icon: UserGroupIcon,
              onClick: () => navigate('/clients/new')
            }}
            secondaryAction={{
              label: "Import from CSV",
              icon: BuildingOfficeIcon,
              onClick: () => navigate('/clients')
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Relocation Workspace</h1>
          <p className="text-muted-foreground mt-2">Select a client to view their comprehensive relocation journey</p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
            />
          </div>
        </div>

        {/* Client Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => {
            const stage = getRelocationStage(client);
            const daysInStage = getDaysInStage(client);
            const statusColor = getClientStatusColor(client.status || 'active');

            return (
              <Card
                key={client._id}
                className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => {
                  console.log('🖱️ Client card clicked:', client._id);
                  console.log('🖱️ Navigating to:', `/relocation-workspace/${client._id}`);
                  navigate(`/relocation-workspace/${client._id}`);
                }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <UserGroupIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{client.name}</h3>
                      <p className="text-sm text-muted-foreground">{client.email}</p>
                    </div>
                  </div>
                  <Badge className={statusColor}>
                    {client.status || 'active'}
                  </Badge>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <MapPinIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Stage: {stage.replace('_', ' ')}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {daysInStage} days in current stage
                    </span>
                  </div>

                  {client.arrivalDate && (
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Arrives: {new Date(client.arrivalDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}

                  {client.familyMembers && client.familyMembers.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <UserGroupIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {client.familyMembers.length} family member(s)
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-input">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-xs text-muted-foreground">Relocation in progress</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center space-x-1"
                    >
                      <span>Open Workspace</span>
                      <ArrowRightIcon className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {filteredClients.length === 0 && searchTerm && (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No clients found</h3>
            <p className="text-muted-foreground mb-4">No clients match your search term "{searchTerm}"</p>
            <Button
              variant="outline"
              onClick={() => setSearchTerm('')}
            >
              Clear Search
            </Button>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8 p-6 bg-card rounded-lg border border-input focus:ring-2 focus:ring-ring focus:border-ring">
          <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/clients/new')}
              className="flex items-center space-x-2"
            >
              <UserGroupIcon className="h-4 w-4" />
              <span>Add New Client</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/clients')}
              className="flex items-center space-x-2"
            >
              <BuildingOfficeIcon className="h-4 w-4" />
              <span>View All Clients</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/relocation-workflows')}
              className="flex items-center space-x-2"
            >
              <MapPinIcon className="h-4 w-4" />
              <span>Relocation Templates</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelocationWorkspaceSelector;
